import React, { useState, useContext, useRef } from "react"; // Adicione useRef
import * as S from "./NovoRelatorioStyle";
import SideBarBox from "../Sidebar/SideBarBox";
import { AuthContext } from "../../context/AuthContext";
import { usePulse } from "../../context/LoadContext";
import {
  formatCNPJ,
  formatCPF,
  formatNumber,
  formatDateSystem,
} from "../../assets/utils";
import { FiFileText, FiDownload, FiLoader } from "react-icons/fi";
import { useMessage } from "../../context/MessageContext";
import { reportService } from "../../Services/reportService";

// Importe as novas dependências e o template
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PDFReportTemplate from "./PDFReportTemplate";

export default function NovoRelatorio() {
  const { userData } = useContext(AuthContext);
  const { showPulse, hidePulse } = usePulse();
  const { showMessage } = useMessage();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Crie uma ref para o nosso template de PDF
  const reportTemplateRef = useRef(null);

  const handleFetchReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);
    try {
      // A busca de dados continua a mesma
      const data = await reportService.getAnnualReport(selectedYear); // userData.id não é mais necessário aqui
      setReportData(data);
    } catch (err) {
      setError(
        "Não foi possível carregar o relatório. Verifique o ano ou tente novamente."
      );
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportData || !reportTemplateRef.current) return;

    setIsDownloading(true);
    showPulse();

    try {
      const canvas = await html2canvas(reportTemplateRef.current, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = imgWidth / imgHeight;
      const widthInPdf = pdfWidth - 20;
      const heightInPdf = widthInPdf / ratio;

      // Se a imagem cabe em uma página, adiciona normalmente
      if (heightInPdf <= pdfHeight) {
        pdf.addImage(imgData, "PNG", 10, 10, widthInPdf, heightInPdf);
      } else {
        // Calcula quantas páginas são necessárias
        const numPages = Math.ceil(heightInPdf / pdfHeight);
        const pageHeightInPixels = (canvas.height / numPages);

        // Para cada página, cria um canvas com a seção correspondente
        for (let i = 0; i < numPages; i++) {
          if (i > 0) {
            pdf.addPage();
          }

          // Cria um canvas temporário para cada seção
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = canvas.width;
          tempCanvas.height = pageHeightInPixels;

          const ctx = tempCanvas.getContext("2d");
          const img = new Image();

          await new Promise((resolve) => {
            img.onload = () => {
              ctx.drawImage(
                img,
                0,
                i * pageHeightInPixels,
                canvas.width,
                pageHeightInPixels,
                0,
                0,
                canvas.width,
                pageHeightInPixels
              );
              resolve();
            };
            img.src = imgData;
          });

          const pageImgData = tempCanvas.toDataURL("image/png");
          pdf.addImage(pageImgData, "PNG", 10, 10, widthInPdf, pdfHeight - 10);
        }
      }

      pdf.save(`Relatorio_Valorizacao_${selectedYear}.pdf`);

      showMessage(`Download iniciado com sucesso.`, "success");
    } catch (err) {
      showMessage(`Erro ao gerar o PDF. Tente novamente.`, "warning");
      console.error("Erro ao gerar PDF no front-end:", err);
    } finally {
      setIsDownloading(false);
      hidePulse();
    }
  };

  // O renderContent permanece o mesmo
  const renderContent = () => {
    if (isLoading)
      return (
        <S.InfoMessage>
          <FiLoader size={32} className="spin" /> Carregando dados...
        </S.InfoMessage>
      );
    if (error) return <S.InfoMessage>{error}</S.InfoMessage>;
    if (!reportData)
      return (
        <S.InfoMessage>
          Selecione um ano e gere o relatório para visualizar os dados.
        </S.InfoMessage>
      );
    if (!reportData.contracts || reportData.contracts.length === 0)
      return (
        <S.InfoMessage>
          Nenhuma Valoriazação encontrada para o ano de {selectedYear}.
        </S.InfoMessage>
      );

    const totalProfit = reportData.contracts.reduce(
      (acc, item) => acc + item.profitInYearValue,
      0
    );

    return (
      <>
        <S.ReportHeader>
          <div>
            <h3>Resultados para {selectedYear}</h3>
            <S.ClientInfo>
              {reportData.clientName} (
              {reportData.clientDocument.length === 11
                ? formatCPF(reportData.clientDocument)
                : formatCNPJ(reportData.clientDocument)}
              )
            </S.ClientInfo>
          </div>
          <S.ActionButton onClick={handleDownloadPDF} disabled={isDownloading}>
            {isDownloading ? (
              <>
                <FiLoader className="spin" /> Baixando...
              </>
            ) : (
              <>
                <FiDownload /> Baixar PDF
              </>
            )}
          </S.ActionButton>
        </S.ReportHeader>
        <S.StyledTable>
          <thead>
            <tr>
              <th>Contrato ID</th>
              <th>Valor de Compra</th>
              <th>Data da Compra</th>
              <th>Início Valorização</th>
              <th>Término Previsto</th>
              <th>Valorização no Período (R$)</th>
            </tr>
          </thead>
          <tbody>
            {reportData.contracts.map((item) => (
              <tr key={item.contractId}>
                <td>#{item.contractId}</td>
                <td>R${formatNumber(item.totalPrice)}</td>
                <td>{formatDateSystem(item.dateCreated)}</td>
                <td>{formatDateSystem(item.firstValorization)}</td>
                <td>{formatDateSystem(item.endContractDate)}</td>
                <td>R$ {formatNumber(item.profitInYearValue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="4">VALORIZAÇÃO TOTAL EM {selectedYear}</td>
              <td>R$ {formatNumber(totalProfit)}</td>
            </tr>
          </tfoot>
        </S.StyledTable>
      </>
    );
  };

  return (
    <SideBarBox>
      {/* Div para esconder o template do PDF */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <PDFReportTemplate ref={reportTemplateRef} reportData={reportData} />
      </div>

      <S.PageContainer>
        <S.Header>
          <S.Title>
            <FiFileText /> Relatório de Valorização
          </S.Title>
          <S.Subtitle>
            Veja o desempenho dos seus contratos e a valorização obtida em um ano
            específico.
          </S.Subtitle>
        </S.Header>

        <S.Controls>
          <S.YearInput
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            placeholder="Ano"
          />
          <S.ActionButton onClick={handleFetchReport} disabled={isLoading}>
            {isLoading ? (
              <>
                <FiLoader className="spin" /> Gerando...
              </>
            ) : (
              "Gerar Relatório"
            )}
          </S.ActionButton>
        </S.Controls>

        <S.ReportDisplay>{renderContent()}</S.ReportDisplay>
      </S.PageContainer>
    </SideBarBox>
  );
}
